import React, {
    forwardRef,
    useEffect,
    useRef,
    useState,
    type KeyboardEventHandler,
    type ChangeEvent,
    type MouseEvent
} from 'react';

import classnames from 'classnames';

import { INPUT_CLEAR_BUTTON_CONTAINER_TEST_ID, INPUT_CONTAINER } from '../../../dataTestIds';
import { useConfig } from '../../features/config';
import useTranslation from '../../features/locale/useTranslation';
import { useId } from '../../hooks/useId';
import { useMergeRefs } from '../../hooks/useMergeRefs';
import { resolveOnChangeEvent } from '../../utils';
import { FieldLabel } from '../FieldLabel';
import { FormControlClearButton } from '../FormControlClearButton';
import { FormControlMessage } from '../FormControlMessage';
import { Icon } from '../Icon';
import { IconButton } from '../IconButton';
import { Small } from '../Typography';

import { type InputProps } from './Input.types';

import styles from './Input.module.scss';

export const Input = forwardRef<HTMLInputElement, InputProps>((props: InputProps, ref) => {
    const {
        allowClear: receivedAllowClear,
        autoFocus = false,
        className,
        clearIconProps,
        disabled,
        error,
        id,
        inputClassName,
        label,
        maxLength,
        onChange,
        onClear,
        onPressEnter,
        onKeyDown,
        onTogglePasswordVisibility,
        placeholder,
        readOnlyPlaceholder,
        readOnly,
        size = 'default',
        togglePasswordVisibilityBtnId,
        tooltip,
        tooltipPlacement,
        type = 'text',
        value,
        defaultValue,
        showCount,
        prefix,
        suffix,
        min,
        max,
        step,
        autoComplete,
        ...rest
    } = props;

    const { t } = useTranslation();
    const inputId = useId(id);
    const tooltipId = tooltip ? `${inputId}-tooltip` : '';
    const { defaultAllowClear } = useConfig();
    const innerRef = useRef<HTMLInputElement | null>(null);
    const mergedRefs = useMergeRefs([ref, innerRef]);

    const isPassword = type === 'password';
    const isRange = type === 'range';
    const isControlled = value !== undefined;
    const computedAllowClear = receivedAllowClear ?? defaultAllowClear;
    const allowClear = !isPassword && !isRange && !readOnly && !disabled && computedAllowClear;
    const withSuffix = !!suffix;
    const isClearButtonHidden = withSuffix || !computedAllowClear || isPassword || isRange;

    const [passwordVisible, setPasswordVisible] = useState(false);
    let actualType = type;
    if (isPassword) actualType = passwordVisible ? 'text' : 'password';

    const [uncontrolledValue, setUncontrolledValue] = useState(() =>
        isControlled ? '' : (defaultValue ?? '')
    );

    const displayValue = isControlled ? value! : uncontrolledValue;
    const isClearButtonVisible = allowClear && !!displayValue;

    useEffect(() => {
        const focus = () => autoFocus && innerRef.current?.focus();
        if (process.env.NODE_ENV === 'test') {
            focus();
        } else {
            setTimeout(() => focus(), 0);
        }
    }, [autoFocus]);

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        const newVal = e.target.value;
        if (!isControlled) {
            setUncontrolledValue(newVal);
        }
        onChange?.(e);
    };

    const handleClearClick = (e: React.MouseEvent<HTMLElement>) => {
        onClear?.();

        const inputElement = innerRef.current;
        if (!inputElement) return;
        inputElement.value = '';

        if (!isControlled) {
            setUncontrolledValue('');
        }

        if (onChange) {
            const syntheticEvent = resolveOnChangeEvent(inputElement, e as never);
            onChange(syntheticEvent);
        }
    };

    const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
        if (e.key === 'Enter') {
            onPressEnter?.(e);
        }
        onKeyDown?.(e);
    };

    const clearButton = (
        <span
            className={classnames(styles.clearButton, {
                [styles.clearButtonHidden]: !isClearButtonVisible,
                [styles.notAllowedClear]: isClearButtonHidden,
                [styles.hasSuffix]: withSuffix || showCount
            })}
            data-testid={INPUT_CLEAR_BUTTON_CONTAINER_TEST_ID}
        >
            <FormControlClearButton
                {...clearIconProps}
                tabIndex={isClearButtonHidden ? -1 : clearIconProps?.tabIndex}
                onClick={handleClearClick}
                // prevent input blur event on clear button click
                onMouseDown={(event: MouseEvent<HTMLElement>) => event.preventDefault()}
            />
        </span>
    );

    const renderPasswordToggle = () =>
        isPassword ? (
            <IconButton
                size="medium"
                type="ghost"
                color="neutral"
                aria-label={passwordVisible ? 'hide password' : 'show password'}
                id={togglePasswordVisibilityBtnId}
                onClick={() => {
                    onTogglePasswordVisibility?.();
                    setPasswordVisible((v) => !v);
                }}
            >
                <Icon name={passwordVisible ? 's-icon-eye' : 's-icon-eye-slash'} />
            </IconButton>
        ) : null;

    return (
        <div
            className={classnames(styles.container, className)}
            data-testid={rest.name ? INPUT_CONTAINER(rest.name) : undefined}
        >
            {label && (
                <FieldLabel
                    htmlFor={inputId}
                    label={label}
                    tooltip={tooltip}
                    tooltipId={tooltipId}
                    tooltipPlacement={tooltipPlacement}
                />
            )}
            <span
                className={classnames(
                    styles.inputWrapper,
                    {
                        [styles.error]: !!error,
                        [styles.disabled]: disabled,
                        [styles.readOnly]: readOnly,
                        [styles.small]: size === 'small',
                        [styles.range]: isRange,
                        [styles.allowClear]: allowClear,
                        [styles.maxLengthReached]:
                            maxLength && value?.toString().length === maxLength
                    },
                    inputClassName
                )}
            >
                {prefix && <span className={styles.prefix}>{prefix}</span>}
                <input
                    {...rest}
                    id={inputId}
                    ref={mergedRefs}
                    type={actualType}
                    className={styles.inputField}
                    placeholder={
                        readOnly && !value ? (readOnlyPlaceholder ?? t('None')) : placeholder
                    }
                    disabled={disabled}
                    readOnly={readOnly}
                    maxLength={maxLength}
                    min={min}
                    max={max}
                    step={step}
                    autoComplete={autoComplete}
                    value={displayValue}
                    onChange={(e) => handleChange?.(e as any)}
                    onKeyDown={handleKeyDown}
                    aria-invalid={!!error}
                />
                <span className={styles.suffix}>
                    {clearButton}
                    {renderPasswordToggle()}
                    {suffix}
                    {showCount && (
                        <Small className={styles.count}>
                            {maxLength && maxLength > 0
                                ? `${displayValue?.toString().length ?? 0} / ${maxLength}`
                                : `${displayValue?.toString().length ?? 0}`}
                        </Small>
                    )}
                </span>
            </span>
            {error && typeof error === 'string' && <FormControlMessage message={error} />}
        </div>
    );
});

Input.displayName = 'Input';
